import { useEffect, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import Swal from "sweetalert2"
import {
  getFollowUpReminderListAction,
  updateFollowUpReminderAction,
} from "../../../../../store/actions/FollowUpReminderAction"
import { formatDate } from "../../../../../utils/dateUtils"

// ✅ Your PNG icons
import completeIcon from "/src/assets/images/check_11601272.png"
import cancelIcon from "/src/assets/images/icons8-cancel-96.png"


const ServiceCallerFollowupReminder = () => {
  const dispatch = useDispatch()

  const {
    data: followupList = [],
    loading = false,
    error = null,
    updating = false,
  } = useSelector((state) => state.followUpReminder || {})

  const fetchReminders = useCallback(() => {
    dispatch(getFollowUpReminderListAction())
  }, [dispatch])

  useEffect(() => {
    fetchReminders()
  }, [fetchReminders])

  const handleStatusUpdate = async (reminderId, status, statusText) => {
    if (updating) return

    const result = await Swal.fire({
      title: `Mark as ${statusText}?`,
      text: `This follow-up will be marked as ${statusText.toLowerCase()}.`,
      icon: status === "CANCELLED" ? "warning" : "question",
      showCancelButton: true,
      confirmButtonColor: status === "CANCELLED" ? "#d33" : "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: `Yes, mark as ${statusText.toLowerCase()}`,
    })

    if (result.isConfirmed) {
      try {
        const response = await dispatch(
          updateFollowUpReminderAction(reminderId, status)
        )
        if (response.success) {
          Swal.fire(
            `${statusText}!`,
            `The follow-up has been marked as ${statusText.toLowerCase()}.`,
            "success"
          )
        } else {
          Swal.fire(
            "Error!",
            response.error || "Failed to update reminder",
            "error"
          )
        }
      } catch (error) {
        Swal.fire(
          "Error!",
          "Something went wrong while updating the reminder",
          "error"
        )
      }
    }
  }

  const handleMarkAsDone = (reminderId) => {
    handleStatusUpdate(reminderId, "DONE", "Done")
  }

  const handleCancel = (reminderId) => {
    handleStatusUpdate(reminderId, "CANCELLED", "Cancelled")
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "DONE":
        return <span className="badge bg-success">Done</span>
      case "CANCELLED":
        return <span className="badge bg-danger">Cancelled</span>
      case "PENDING":
      default:
        return <span className="badge bg-warning">Pending</span>
    }
  }

  // Loader
  if (loading) {
    return (
      <div className="mt-3 text-center py-4">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2">Loading follow-up reminders...</p>
      </div>
    )
  }

  // Error
  if (error) {
    return (
      <div className="mt-3 alert alert-danger">
        <strong>Error:</strong> {error}
      </div>
    )
  }

  return (
    <div className="mt-3">
      {followupList.length > 0 ? (
        <div className="row">
          {followupList.map((item, index) => (
            <div
              key={item.reminder_id || index}
              className="col-md-4 mb-3"
            >
              <div className="custom-timeline-panel timeline-panel bgl-dark border-0 p-3 rounded shadow-sm h-100 position-relative">

                {/* ✅ Icons only (no buttons) */}
                {item.reminder_status === "PENDING" && (
                  <div className="position-absolute top-0 end-0 p-2 d-flex gap-2">

                    <img
                      src={completeIcon}
                      alt="Done"
                      title="Mark as Done"
                      style={{
                        width: "21px",
                        height: "21px",
                        cursor: "pointer",
                        transition: "transform 0.2s"
                      }}
                      onClick={() => handleMarkAsDone(item.reminder_id)}
                      onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.2)")}
                      onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
                    />

                    <img
                      src={cancelIcon}
                      alt="Cancel"
                      title="Cancel"
                      style={{
                        width: "22px",
                        height: "22px",
                        cursor: "pointer",
                        transition: "transform 0.2s"
                      }}
                      onClick={() => handleCancel(item.reminder_id)}
                      onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.2)")}
                      onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
                    />

                  </div>
                )}

                {/* ✅ Content unchanged */}
                <h6 className="font-weight-bold mb-2">
                  Follow-up Reminder #{item.reminder_id}
                </h6>

                <p className="mb-1">
                  <strong>Follow-up Date:</strong> {formatDate(item.reminder_date)}
                </p>

                <p className="mb-1">
                  <strong>Follow-up Time:</strong> {item.reminder_time}
                </p>

                <p className="mb-2">
                  <strong>Status:</strong> {getStatusBadge(item.reminder_status)}
                </p>

              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-muted text-center py-4">
          No follow-up reminders available
        </div>
      )}
    </div>
  )
}

export default ServiceCallerFollowupReminder